import type { LucideIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";
import { RangeInput, type RangeValue } from "@/components/ui/range-input";

type Props = {
  name: string;
  label?: string;
  description?: string;
  placeholder: string;
  icon?: LucideIcon;
  onChange?: (value: RangeValue | null) => void;
  formItemClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
  initialMode?: "single" | "range";
  singleTabTitle?: string;
  rangeTabTitle?: string;
  singleLabel?: string;
  minLabel?: string;
  maxLabel?: string;
  singlePlaceholder?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  highlightWhenSelected?: boolean;
};

export const NumberRangeInput = ({
  name,
  label,
  description,
  placeholder,
  icon,
  onChange,
  formItemClassName,
  initialMode,
  singleTabTitle,
  rangeTabTitle,
  singleLabel,
  minLabel,
  maxLabel,
  singlePlaceholder,
  minPlaceholder,
  maxPlaceholder,
  highlightWhenSelected = true,
}: Props) => {
  const form = useFormContext();
  const { error } = useFormField();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const handleOnChange = (value: RangeValue | null) => {
          field.onChange(value);
          onChange?.(value);
        };

        const rangeValue = field.value ?? null;

        return (
          <FormItem className={formItemClassName}>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <RangeInput
                placeholder={placeholder}
                value={rangeValue}
                onChange={handleOnChange}
                icon={icon}
                initialMode={initialMode}
                singleTabTitle={singleTabTitle}
                rangeTabTitle={rangeTabTitle}
                singleLabel={singleLabel}
                minLabel={minLabel}
                maxLabel={maxLabel}
                singlePlaceholder={singlePlaceholder}
                minPlaceholder={minPlaceholder}
                maxPlaceholder={maxPlaceholder}
                aria-invalid={error ? true : undefined}
                highlightWhenSelected={highlightWhenSelected}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
